/**
 * Unified Analytics Service
 * Sends events to both Google Analytics and Microsoft Clarity
 */

import {
  identifyUser as clarityIdentifyUser,
  setClarityTag,
  trackClarityEvent,
} from './clarity';
import * as gtag from './gtag';
import { trackWebsiteSessionEvent } from '@/session-replay';
import type { WebsiteSessionEventNameType } from '@/session-replay/session-replay.model';

// ============================================
// User Identification
// ============================================

export function identifyUser(
  userId: string,
  userData?: { name?: string; email?: string }
): void {
  // Microsoft Clarity
  clarityIdentifyUser(userId, {
    friendlyName: userData?.name,
    email: userData?.email,
  });

  // Google Analytics - set user properties
  gtag.setUserProperties({
    user_id: userId,
    user_email: userData?.email,
    user_name: userData?.name,
  });
}

export function setTag(key: string, value: string): void {
  setClarityTag(key, value);
}

// ============================================
// Form Events
// ============================================

export function formStart(
  formName: string,
  language: string = 'en',
  data?: Record<string, unknown>
): void {
  trackWebsiteSessionEvent('form_start', formName, { language, ...data });

  gtag.event('form_start', {
    event_category: 'Form',
    event_label: formName,
    custom_parameters: {
      form_name: formName,
      language,
      ...data,
    },
  });

  trackClarityEvent(`form_start_${formName}`);
  setClarityTag('form_name', formName);
}

export function formSubmission(
  formName: string,
  language: string = 'en',
  data?: Record<string, unknown>
): void {
  trackWebsiteSessionEvent('form_submit', formName, { language, ...data });

  gtag.event('form_submit', {
    event_category: 'Form',
    event_label: formName,
    custom_parameters: {
      form_name: formName,
      language,
      ...data,
    },
  });

  trackClarityEvent(`form_submit_${formName}`);
  setClarityTag('form_submitted', formName);
}

/** The visitor pressed submit but delivery failed (they saw an error). */
export function formSubmitFailed(
  formName: string,
  language: string = 'en',
  error?: string
): void {
  trackInteraction('form_submit_failed', formName, {
    language,
    error: error?.slice(0, 200) ?? null,
  });
}

export function formError(
  formName: string,
  errorFields: string[],
  language: string = 'en'
): void {
  trackWebsiteSessionEvent('form_error', formName, {
    error_fields: errorFields,
    language,
  });

  gtag.event('form_error', {
    event_category: 'Form',
    event_label: `${formName}:${errorFields.join(',')}`,
    custom_parameters: {
      form_name: formName,
      error_fields: errorFields.join(','),
      language,
    },
  });

  trackClarityEvent(`form_error_${formName}`);
}

// ============================================
// CTA Events
// ============================================

export function ctaClick(
  ctaName: string,
  location: string,
  language: string,
  destination: string
): void {
  const target = new URL(destination, window.location.href);
  // Group routes without campaign parameters or demo authentication tokens.
  const ctaDestination =
    (target.origin === window.location.origin ? '' : target.origin) +
    target.pathname +
    target.hash;
  trackWebsiteSessionEvent('cta_click', ctaName, {
    location,
    language,
    destination: ctaDestination,
  });

  gtag.event('cta_click', {
    event_category: 'CTA',
    event_label: ctaName,
    custom_parameters: {
      cta_name: ctaName,
      cta_location: location,
      cta_destination: ctaDestination,
      page_path: window.location.pathname,
      language,
    },
  });

  trackClarityEvent(`cta_${ctaName}`);
  setClarityTag('cta_clicked', ctaName);
}

// ============================================
// Pricing Events
// ============================================

export function planSelect(
  planName: string,
  planPrice: number,
  billingCycle: 'monthly' | 'yearly',
  currency: string,
  language: string = 'en'
): void {
  trackWebsiteSessionEvent('plan_select', planName, {
    billing_cycle: billingCycle,
    currency,
    language,
    price: planPrice,
  });

  gtag.event('select_item', {
    event_category: 'Pricing',
    event_label: planName,
    currency,
    value: planPrice,
    items: [
      {
        item_id: planName.toLowerCase().replace(/\s+/g, '_'),
        item_name: planName,
        category: 'pricing_plan',
        price: planPrice,
        currency,
      },
    ],
    custom_parameters: {
      plan_name: planName,
      billing_cycle: billingCycle,
      language,
    },
  });

  trackClarityEvent(
    `plan_select_${planName.toLowerCase().replace(/\s+/g, '_')}`
  );
  setClarityTag('selected_plan', planName);
}

// ============================================
// Chat Events
// ============================================

export function chatOpen(
  language: string = 'en',
  /** What opened it: button, unread_popup, popup_quick_reply. */
  source: string = 'button'
): void {
  trackWebsiteSessionEvent('chat_opened', source, { language, source });

  gtag.event('chat_open', {
    event_category: 'Chat',
    event_label: 'Widget Opened',
    custom_parameters: { language },
  });

  trackClarityEvent('chat_open');
}

export function chatMessage(
  isUserMessage: boolean,
  language: string = 'en',
  /** How the visitor sent it: typed, quick_reply, popular_action, popup. */
  source?: string
): void {
  const eventName = isUserMessage
    ? 'chat_message_sent'
    : 'chat_message_received';

  trackWebsiteSessionEvent(eventName, source ?? null, {
    language,
    ...(source ? { source } : {}),
  });

  gtag.event(eventName, {
    event_category: 'Chat',
    event_label: isUserMessage ? 'User Message' : 'Bot Response',
    custom_parameters: {
      language,
      message_type: isUserMessage ? 'user' : 'bot',
    },
  });

  trackClarityEvent(eventName);
}

export function helpFeedback(
  articleSlug: string | undefined,
  feedback: 'happy' | 'neutral' | 'sad',
  language: string = 'en'
): void {
  trackWebsiteSessionEvent('help_feedback', articleSlug ?? null, {
    feedback,
    language,
  });
}

// ============================================
// Interaction Events (AI demos, booking demo, chat lifecycle)
// ============================================

/**
 * Records an interaction in php-backend website telemetry and Google Analytics.
 * `value` is the short, queryable label (e.g. a scenario or CTA name); `data`
 * carries the details. Never pass message text or personal details here.
 */
export function trackInteraction(
  type: WebsiteSessionEventNameType,
  value: string | null = null,
  data: Record<string, unknown> = {}
): void {
  trackWebsiteSessionEvent(type, value, data);
  gtag.event(type, {
    event_category: 'Interaction',
    event_label: value ?? undefined,
    custom_parameters: data,
  });
}
